// src/app/services/mbrs/validation-field-key.util.ts

/**
 * The two column-key formats a financial sheet can use. Named so the
 * discriminator is referenced by symbol (COLUMN_FORMAT.DUAL) rather than a
 * bare string literal that could be mistyped silently.
 */
export const COLUMN_FORMAT = {
  /** `qname[Consolidated][CurrentYear]` — 4 plain columns. */
  DUAL: 'dualColumn',
  /** `qname:<EquityMember>[Consolidated][CurrentYear]` — equity columns (SOCIE). */
  EQUITY: 'equityColumns',
} as const;

export type ColumnFormat = (typeof COLUMN_FORMAT)[keyof typeof COLUMN_FORMAT];

/** Maps state-service sheet keys to their column-key format. */
export const SHEET_COLUMN_TYPE_MAP: { [key: string]: ColumnFormat } = {
  statementOfFinancialPosition: COLUMN_FORMAT.DUAL,
  subClassificationOfAssetsLiabilitiesEquity: COLUMN_FORMAT.DUAL,
  subClassificationLiquidity: COLUMN_FORMAT.DUAL,
  statementOfProfitOrLoss: COLUMN_FORMAT.DUAL,
  analysisOfProfitOrLoss: COLUMN_FORMAT.DUAL,
  statementOfProfitOrLossByNature: COLUMN_FORMAT.DUAL,
  analysisOfProfitOrLossByNature: COLUMN_FORMAT.DUAL,
  statementOfComprehensiveIncome: COLUMN_FORMAT.DUAL,
  statementOfComprehensiveIncomeBeforeTax: COLUMN_FORMAT.DUAL,
  statementOfCashFlowsIndirectMethod: COLUMN_FORMAT.DUAL,
  statementOfChangesInEquity: COLUMN_FORMAT.EQUITY,
};

/** Round to MBRS display precision (2 decimals) before an equality comparison. */
export function toComparisonPrecision(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The single, codebase-wide tolerance for numeric value comparisons — both
 * 1:1 field equality ("value maps") AND sum-aggregation checks (a total vs the
 * sum of its components). Unified at one cent (`0.01`) across all sheets so the
 * rounding policy is never "0.01 here, 0.001 there".
 *
 * The comparison model is the same for every operand:
 *   • sum-derived fields → perform all calculations, THEN round to 2 d.p., then compare;
 *   • input / N8N-extracted fields → round to 2 d.p., then compare.
 * Both operands are rounded to the 2-decimal MBRS display precision via
 * {@link toComparisonPrecision} before the difference is taken, so a value like
 * `1500.504` (an un-rounded extracted figure) compares equal to the `1500.50`
 * the user sees. The `0.01` band then absorbs the at-most-one-cent rounding
 * drift that can accumulate when many 2-d.p. components are summed.
 */
export const MBRS_EQUALITY_TOLERANCE = 0.01;

/**
 * Absolute difference between two numbers AFTER both are rounded to the 2-d.p.
 * MBRS display precision. This is the canonical way to diff two values that
 * should be equal — every equality/value-map comparator should use it so the
 * codebase shares one rounding policy rather than each path choosing raw vs
 * rounded and 0.001 vs 0.01.
 */
export function comparisonDifference(first: number, second: number): number {
  return Math.abs(toComparisonPrecision(first) - toComparisonPrecision(second));
}

/** A numeric-ish value as integer cents (null / '' / NaN → 0). */
function toCents(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
}

/**
 * True when two values differ at one-cent precision — the comparator for
 * SAME-FIELD equality, where two calculated totals must be identical (e.g.
 * `ProfitLoss` ↔ `ProfitLoss_2`, `ComprehensiveIncome` ↔ `ComprehensiveIncome_2`).
 *
 * Two important differences from `comparisonDifference(a, b) > MBRS_EQUALITY_TOLERANCE`:
 *
 *  1. **Detects a 0.01 difference.** After rounding to 2 d.p. a difference of
 *     exactly one cent is `0.01`, which is NOT `> 0.01`, so the tolerance form
 *     only flags ≥ 0.02. Same-field totals carry no summation drift, so any
 *     cent of difference is a real mismatch — matching the editor's
 *     `Math.round(a*100)/100 !== Math.round(b*100)/100`.
 *  2. **Float-precision safe.** It compares INTEGER cents instead of doing a
 *     `Math.round(x*100)/100` round-trip, so two values that are equal to the
 *     cent can never mis-compare as unequal.
 *
 * Companion {@link diffMeetsTolerance} applies the same cent-precision policy at
 * call sites that already hold a precomputed `comparisonDifference(a, b)`. The
 * policy is applied uniformly — value-maps, cross-sheet equality AND sum
 * aggregations — so any one-cent discrepancy is a real, reportable error.
 */
export function differsAtCentPrecision(first: unknown, second: unknown): boolean {
  return toCents(first) !== toCents(second);
}

/**
 * Companion to {@link differsAtCentPrecision} for call sites that already hold a
 * precomputed, 2-d.p.-rounded absolute difference (e.g. `comparisonDifference(a, b)`).
 * True when that difference is at least the tolerance (default one cent).
 *
 * Compares INTEGER cents, so a rounded difference of exactly `0.01` IS flagged —
 * the old `difference > tolerance` missed it (a rounded `0.01` is not `> 0.01`,
 * giving an effective 0.02 band) — and there is no float round-trip edge case.
 */
export function diffMeetsTolerance(
  roundedDifference: number,
  tolerance: number = MBRS_EQUALITY_TOLERANCE
): boolean {
  return Math.round(roundedDifference * 100) >= Math.round(tolerance * 100);
}

/** A form-shaped value that counts as "empty" for required-field checks.
 *  Single source for the former per-sheet isRole<N>Empty() one-liners. */
export function isEmptyCell(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Human-readable label for a bracket column suffix. */
export function getColumnLabel(suffix: string): string {
  const labels: { [key: string]: string } = {
    '[Consolidated][CurrentYear]': 'Consolidated Current',
    '[Consolidated][PreviousYear]': 'Consolidated Previous',
    '[Separate][CurrentYear]': 'Separate Current',
    '[Separate][PreviousYear]': 'Separate Previous',
  };
  return labels[suffix] || suffix;
}

/** Visible column suffixes given the filing's consolidation + comparative flags. */
export function getVisibleColumnSuffixes(isConsolidated: boolean, isSubsequent: boolean): string[] {
  const consolidationTypes = isConsolidated ? ['Consolidated', 'Separate'] : ['Separate'];
  const periodTypes = isSubsequent ? ['CurrentYear', 'PreviousYear'] : ['CurrentYear'];
  const suffixes: string[] = [];
  for (const consolidationType of consolidationTypes) {
    for (const periodType of periodTypes) {
      suffixes.push(`[${consolidationType}][${periodType}]`);
    }
  }
  return suffixes;
}

/** Build the form-control key for a field in a given sheet + column. */
export function buildFieldKey(
  fieldQname: string,
  sheetKey: string,
  columnSuffix: string,
  taxonomy: 'MFRS' | 'MPERS'
): string {
  const columnType = SHEET_COLUMN_TYPE_MAP[sheetKey];
  if (columnType === COLUMN_FORMAT.DUAL) {
    return `${fieldQname}${columnSuffix}`;
  }
  if (columnType === COLUMN_FORMAT.EQUITY) {
    const totalColumnQname = taxonomy === 'MFRS' ? 'ifrs-full:EquityMember' : 'ifrs-smes:EquityMember';
    return `${fieldQname}:${totalColumnQname}${columnSuffix}`;
  }
  return fieldQname;
}
